import { getAppConfig } from '@services/config';
import { externalHttpService } from '@services/http';
import { CACHE_TAGS, HttpService } from '@services/http/http';

import { NumeriquesReviewInformation } from '@app-types/numeriques';

type PublicationsServiceConfig = {
    url: string;
    id: string;
    secret: string;
    token: string | null;
    tokenExpiry: number | null;
    ttl: number;
};

export class PublicationsService {
    private httpService: HttpService;
    private configuration: PublicationsServiceConfig;

    public constructor(httpService: HttpService, configuration: PublicationsServiceConfig) {
        this.httpService = httpService;
        this.configuration = configuration;
    }

    private async authenticate(): Promise<void> {
        try {
            const response = await this.httpService.post<{
                token_type: string;
                expires_in: number;
                access_token: string;
            }>(
                `${this.configuration.url}/token`,
                new URLSearchParams({
                    client_id: this.configuration.id,
                    client_secret: this.configuration.secret,
                    grant_type: 'client_credentials',
                }),
                {
                    headers: { 'content-type': 'application/x-www-form-urlencoded' },
                },
            );
            if (response) {
                this.configuration.token = response.access_token;
                this.configuration.tokenExpiry = Date.now() + response.expires_in * 1000;
            }
        } catch (error) {
            console.error('[les numeriques] Cannot authenticate', error);
        }
    }

    private async ensureAuthenticated(): Promise<void> {
        if (
            !this.configuration.token ||
            (this.configuration.tokenExpiry && Date.now() >= this.configuration.tokenExpiry)
        ) {
            await this.authenticate();
            if (!this.configuration.token) {
                throw new Error('[les numeriques] Authentication failed, no token available');
            }
        }
    }

    public async getProductsTestsPublications(
        _idsPublication: string[],
    ): Promise<NumeriquesReviewInformation['publications']> {
        if (_idsPublication.length === 0) {
            return [];
        }

        const idsPublication = _idsPublication.join(',');

        try {
            await this.ensureAuthenticated();

            const { publications } = await this.httpService.get<NumeriquesReviewInformation>(
                `${this.configuration.url}/api/publications?ids=${idsPublication}&types=tests`,
                {},
                {
                    ttl: this.configuration.ttl,
                    tags: [CACHE_TAGS.NUMERIQUES],
                    headers: { Authorization: `Bearer ${this.configuration.token}` },
                },
            );

            publications.map((publication) => {
                if (typeof publication.pluses === 'string') {
                    publication.pluses = publication.pluses.split('\r\n');
                }
                if (typeof publication.minuses === 'string') {
                    publication.minuses = publication.minuses.split('\r\n');
                }
            });

            return publications || [];
        } catch (error) {
            return [];
        }
    }
}

const config = getAppConfig();

export const publicationsService = new PublicationsService(externalHttpService, {
    url: config.numeriques.url,
    id: config.numeriques.clientId,
    secret: config.numeriques.clientSecret,
    token: null,
    tokenExpiry: null,
    ttl: parseInt(config.cache.ttl.numeriques),
});
